import type { FC, KeyboardEventHandler } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Picture } from '@ui';
import InputMask from '@components/InputMask';
import {
  checkPeopleSubscriptionQueryOptions,
  useProductSubscribeMutation
} from '@queries/subscriptions';
import { when } from 'mobx';
import { observer } from 'mobx-react';
import { warningRed, crossImg } from '@images';
import clsx from 'clsx';
import { twcls } from '@utils/twcls';
import { useStores } from '@root/src/stores';
import './index.scss';

interface SubscribeProductProps {
  title?: string;
  subTitle?: string;
  centeredModal?: boolean;
  withPaddingAndBorderRadius?: boolean;
  desktopPopup?: boolean;
  isModal?: boolean;
  productId: number;
  productPageId?: string;
  subscriptionType: string;
  closeModal?: () => void;
}

const SubscribeProduct: FC<SubscribeProductProps> = observer(
  ({
    title = '',
    subTitle,
    centeredModal,
    withPaddingAndBorderRadius,
    desktopPopup,
    isModal,
    productId,
    productPageId,
    subscriptionType,
    closeModal
  }) => {
    const { userStore } = useStores();
    const [email, setEmail] = useState(userStore?.user?.email || '');
    const [phone, setPhone] = useState(userStore?.user?.phone || '');
    const [activeInput, setActiveInput] = useState<'email' | 'phone'>('phone');
    const [error] = useState<string | null>(null);

    const { data: existingSubscription } = useQuery(
      checkPeopleSubscriptionQueryOptions(+productId, subscriptionType)
    );
    // до ответа проверки кнопки заблокированы, как раньше (initial state true)
    const isSubscribedSmsInStock = existingSubscription?.sms ?? true;
    const isSubscribedEmailInStock = existingSubscription?.email ?? true;

    useEffect(() => {
      const disposer = when(
        () => userStore.isLoad,
        () => {
          setEmail(userStore?.user?.email || '');
          setPhone(userStore?.user?.phone || '');
        }
      );
      return () => {
        disposer();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { mutate: subscribeMutate } = useProductSubscribeMutation();

    const subscribe = useCallback((): void => {
      const inputValue = activeInput === 'email' ? email : phone;
      subscribeMutate({
        subscription_type: subscriptionType,
        productId: productId ? +productId : Number(productPageId),
        [activeInput]: inputValue
      });
    }, [
      activeInput,
      email,
      phone,
      subscriptionType,
      productId,
      productPageId,
      subscribeMutate
    ]);

    const pressEnter: KeyboardEventHandler<HTMLInputElement> = useCallback(
      event => {
        if (event.key === 'Enter') subscribe();
      },
      [subscribe]
    );

    const inputValue = activeInput === 'email' ? email : phone;

    return (
      <div
        className={clsx(
          'subscribe-to-product',
          centeredModal && 'subscribe-to-product--centered',
          desktopPopup && 'subscribe-to-product--desktop-popup',
          withPaddingAndBorderRadius && 'subscribe-to-product--with-padding'
        )}
      >
        {isModal && (
          <div className='flex items-center justify-between mb-3'>
            <div className='text-sm font-medium'>Подписаться</div>
            <Picture icon={crossImg} height='24' onClick={closeModal} />
          </div>
        )}
        {title && (
          <div className='subscribe-to-product__main-title'>{title}</div>
        )}
        <div className='subscribe-to-product__sub-title'>{subTitle}</div>
        <div
          className={
            'relative p-0.5 mb-2 rounded-lg bg-[var(--mid-grey)] text-sm leading-none h-10'
          }
        >
          <div
            className={twcls(
              'grid grid-cols-2 h-full ',
              `after:content-[""]
            after:absolute
            after:top-1/2
            after:translate-x-[2px]
            after:-translate-y-1/2
            after:left-0
            after:rounded-[7px]
            after:w-1/2
            after:h-[calc(100%-4px)]
            after:bg-white
            after:z-[1]
            after:rounded-r-lg
            after:transition-all
            after:duration-300`,
              activeInput === 'email' && 'after:translate-x-[calc(100%-2px)]'
            )}
          >
            {[
              { title: 'Телефон', value: 'phone' },
              { title: 'Email', value: 'email' }
            ].map(item => (
              <div
                key={item.value}
                onClick={() => {
                  setActiveInput(item.value as 'email' | 'phone');
                }}
                className={twcls(
                  'relative z-10 cursor-pointer w-full h-full text-center flex items-center justify-center'
                )}
              >
                {item.title}
              </div>
            ))}
          </div>
        </div>
        <div className='grid grid-cols-[auto,158px] allMd:grid-cols-none gap-y-2 gap-x-2'>
          <div>
            {activeInput === 'email' ? (
              <input
                onKeyDown={pressEnter}
                value={inputValue}
                className={twcls(
                  `h-10 py-3 px-4 border border-solid border-white rounded-lg bg-white w-full text-sm`,
                  error && 'border-[#EA4A4A]'
                )}
                placeholder='Email'
                disabled={!!isSubscribedEmailInStock}
                onChange={e => {
                  setEmail(e.target.value);
                }}
              />
            ) : (
              <InputMask
                mask='+7 (999) 999-99-99'
                maskChar=''
                type='tel'
                disabled={!!isSubscribedSmsInStock}
                onKeyPress={pressEnter}
                value={inputValue}
                className={twcls(
                  `h-10 py-3 px-4 border border-solid border-white rounded-lg bg-white w-full text-sm`,
                  error && 'border-[#EA4A4A]'
                )}
                placeholder='Телефон'
                onChange={e => {
                  setPhone(e.target.value);
                }}
              />
            )}

            {error && (
              <div className='subscribe-to-product__warning'>
                <Picture icon={warningRed} height='13' />
                <div className='subscribe-to-product__warning__error'>
                  {error}
                </div>
              </div>
            )}
          </div>
          {activeInput === 'phone' ? (
            <button
              onClick={subscribe}
              className={`${
                isSubscribedSmsInStock
                  ? 'subscribe-to-product__subscribe-btn subscribe-to-product__subscribe-btn--disabled'
                  : 'subscribe-to-product__subscribe-btn'
              }`}
              disabled={!!isSubscribedSmsInStock}
            >
              {isSubscribedSmsInStock === null
                ? ''
                : isSubscribedSmsInStock
                ? 'Вы уже подписаны'
                : 'Сообщить'}
            </button>
          ) : (
            <button
              onClick={subscribe}
              className={`${
                isSubscribedEmailInStock
                  ? 'subscribe-to-product__subscribe-btn subscribe-to-product__subscribe-btn--disabled'
                  : 'subscribe-to-product__subscribe-btn'
              }`}
              disabled={!!isSubscribedEmailInStock}
            >
              {isSubscribedEmailInStock === null
                ? ''
                : isSubscribedEmailInStock
                ? 'Вы уже подписаны'
                : 'Сообщить'}
            </button>
          )}
        </div>
      </div>
    );
  }
);

export default SubscribeProduct;
